import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

import type { TrialLifecycleConflict } from '../trial-lifecycle.types';

export interface GetAllTrialStudentsResponse {
  data: Array<TrialStudent>;
  pagination: Pagination;
}

export interface TrialStudent {
  _id: string;
  firstName: string;
  lastName: string;
  title: string;
  studentName: string;
  email: string;
  contactNo: string;
  calendarName: string;
  notes: string;
  studioId: string;
  calendarId: string;
  contactId: string;
  appointmentStatus: string;
  startTime: string;
  endTime: string;
  eventId: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  __v: number;
  classId: ClassID;
  isPresent: boolean | null;
  lifecycleConflict?: TrialLifecycleConflict | null;
}

export interface ClassID {
  _id: string;
  title: string;
}

export interface Pagination {
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetTrialStudentProps {
  classId?: string;
  date?: string;
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  /** startTime = appointment in range; createdAt = booked in range (dashboard). */
  dateField?: 'startTime' | 'createdAt';
}

export const getAllTrialStudentAPI = async (params?: GetTrialStudentProps) => {
  const response = await baseAPI.get<GetAllTrialStudentsResponse>(
    `/trialstudents`,
    {
      params,
    },
  );
  return response.data;
};

export const getAllTrialStudentQueryOptions = (params?: GetTrialStudentProps) =>
  queryOptions({
    queryKey: QUERY_KEYS.TRIAL_STUDENTS(params),
    queryFn: () => getAllTrialStudentAPI(params),
    placeholderData: keepPreviousData,
    // ENR-2522: 10s poll flooded GET /trialstudents; 60s is enough for dashboard freshness.
    refetchInterval: 60_000,
  });

export const useGetAllTrialStudents = (params?: GetTrialStudentProps) => {
  return useQuery(getAllTrialStudentQueryOptions(params));
};
